<script setup lang="ts">
import { computed, onMounted, reactive, useTemplateRef } from "vue";
import { useRouter } from "vue-router";
import { PageSidebar } from "@easy/ui/page-components";
import { ButtonSecondaryIconSm, ButtonSecondaryMd } from "@easy/ui/buttons";
import { AddIcon, ArrowLeftIcon, LinkShareIcon } from "@easy/ui/icons";
import { FormInput } from "@easy/ui/forms";
import { CardToggle } from "@easy/ui/cards";
import { useIsDirty } from "@/shared/utils/forms/useIsDirty";
import { PageSection } from "@/apps/page/page.interfaces";
import { useActions, useState } from "@/store/vuex-composition-mappers";
import { useFormValidation } from "@/shared/utils/forms/useFormValidation";
import { required } from "@/shared/utils/forms/i18n-vuelidators";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import PageColors from "../PageColors.vue";
import { t } from "@/i18n";

const props = defineProps<{ section: PageSection; backupData: PageSection }>();
const projectNameRef = useTemplateRef<any>("projectName");

defineEmits(["save", "quit"]);

const router = useRouter();
// @ts-expect-error
const { page } = useState("page", ["page"]);
// @ts-expect-error
const { connectToolSectionWithProject } = useActions("page", ["connectToolSectionWithProject"]);
const { isDirty } = useIsDirty(props.backupData, props.section);

const project = reactive({ name: "" });
const { v$, validateForm } = useFormValidation(
    {
        name: { required },
    },
    project
);

const sectionType = computed(() => props.section.type);
const projectData = computed(() => props.section.projectData || { uuid: "" });
const hasProjectConnected = computed(() => Boolean(props.section.projectId));
const editUrl = computed(
    () => `/${sectionType.value}/projects/${projectData.value.uuid}?page=${page.value.uuid}`
);

const title = computed(() => t(`page.${sectionType.value}.title`));
const editTitle = computed(() => t(`page.${sectionType.value}.edit`));

onMounted(() => {
    if (projectNameRef.value) {
        projectNameRef.value?.$el.querySelector("input")?.focus();
    }
});

async function makeNewProject() {
    const isFormCorrect = await validateForm();

    if (!isFormCorrect) {
        return;
    }
    const projectUuid = await connectToolSectionWithProject({
        lang: page.value.lang,
        projectName: project.name,
        tool: sectionType.value,
        sectionId: props.section.id,
    });
    if (projectUuid) {
        await router.push(`/${sectionType.value}/projects/${projectUuid}?page=${page.value.uuid}`);
    }
}
</script>

<template>
    <PageSidebar>
        <template #sidebarTitle>
            <div class="flex items-center space-x-3">
                <ButtonSecondaryIconSm @click="$emit('quit')">
                    <ArrowLeftIcon />
                </ButtonSecondaryIconSm>
                <span class="text-lg truncate">{{ title }}</span>
            </div>
        </template>
        <template #sidebarMain>
            <CardToggle
                :title="
                    hasProjectConnected
                        ? section?.projectData?.name || ''
                        : $t('page.block.content')
                "
            >
                <router-link :to="editUrl" v-if="hasProjectConnected">
                    <ButtonSecondaryMd class="w-full">
                        <LinkShareIcon />
                        <span>{{ editTitle }}</span>
                    </ButtonSecondaryMd>
                </router-link>
                <div v-else>
                    <FormInput
                        ref="projectName"
                        required
                        :title="$t('page.sections.new_project_name')"
                        v-model="project.name"
                        @blur="v$.name.$touch"
                        :error="v$.name.$errors?.[0]?.$message"
                    />
                    <ButtonSecondaryMd class="w-full mt-3" @click="makeNewProject()">
                        <AddIcon />
                        <span>{{ t("page.sections.create_project", { tool: sectionType }) }}</span>
                    </ButtonSecondaryMd>
                </div>
            </CardToggle>
            <CardToggle :title="$t('page.block.styles')">
                <div class="space-y-5">
                    <template v-if="sectionType !== 'love' && sectionType !== 'faq'">
                        <PageColors
                            :title="$t('shared.colors.brand')"
                            v-model="section.styleValues.brand"
                        />
                    </template>
                    <PageColors
                        :title="$t('shared.colors.background')"
                        v-model="section.styleValues.background"
                    />
                    <PageColors
                        :title="$t('shared.colors.text')"
                        v-model="section.styleValues.text"
                    />
                    <PageColors
                        :title="$t('shared.colors.card_background')"
                        v-model="section.styleValues.cardBackground"
                    />
                    <PageColors
                        :title="$t('shared.colors.card_text')"
                        v-model="section.styleValues.cardText"
                    />
                    <PageColors
                        :title="$t('shared.colors.border')"
                        v-model="section.styleValues.border"
                    />
                </div>
            </CardToggle>
        </template>
        <template #sidebarFooter>
            <cSaveButton
                @save="$emit('save')"
                :disabled="!isDirty"
                :ctaDisabled="$t('shared.save')"
            />
        </template>
    </PageSidebar>
</template>
